// Importaciones de React, librerías y recursos.
import React, { useState, useRef } from 'react';
import Swal from 'sweetalert2'; // Para mostrar alertas y modales atractivos.
import ReCAPTCHA from "react-google-recaptcha"; // Componente para el reCAPTCHA de Google.
import logoImage from '../img/logo.png';
import edificiosImage from '../img/edificios.jpg';
import './FormularioGeneral.css'; // Estilos específicos para este formulario.

/**
 * Componente FormularioGeneral.
 * Renderiza un formulario de contacto general con validación de correo,
 * protección reCAPTCHA y envío de datos a un webhook.
 */
const FormularioGeneral = () => {
  // Estado para almacenar los datos de los campos del formulario.
  const [formData, setFormData] = useState({
    nombre: '',
    correo: '',
    mensaje: ''
  });

  // Referencia para acceder al componente ReCAPTCHA y sus métodos (como .getValue() y .reset()).
  const recaptchaRef = useRef();

  // Manejador para actualizar el estado `formData` cada vez que un campo del formulario cambia.
  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  /**
   * Valida el formato del correo electrónico y restringe proveedores gratuitos.
   * @param {string} email - El correo electrónico a validar.
   * @returns {boolean} - `true` si el correo es válido y no es de un proveedor gratuito, `false` en caso contrario.
   */
  const validateEmail = (email) => {
    const emailRegex = /^[a-zA-Z0-9._%+-]+@([a-zA-Z0-9-]+\.)+[a-zA-Z]{2,}$/;
    const invalidProviders = ['gmail', 'yahoo', 'outlook', 'hotmail'];
    const domain = email.split('@')[1]?.split('.')[0]; // Obtiene el dominio principal (ej. "gmail").
    // Retorna true solo si el formato es válido Y el dominio no está en la lista de inválidos.
    return emailRegex.test(email) && !invalidProviders.includes(domain);
  };

  // Manejador para el envío del formulario.
  const handleFormSubmit = async (e) => {
    e.preventDefault(); // Previene el comportamiento por defecto del formulario (recargar la página).

    // 1. Validar reCAPTCHA
    const recaptchaValue = recaptchaRef.current.getValue();
    if (!recaptchaValue) {
      Swal.fire('Captcha no completado', 'Por favor, completa el captcha antes de enviar.', 'warning');
      return;
    }
    
    // 2. Validar correo electrónico corporativo
    if (formData.correo && !validateEmail(formData.correo)) {
      Swal.fire('Correo no válido', 'Te aconsejamos que introduzcas un correo electrónico de empresa para poder enviar tu solicitud.', 'warning');
      return;
    }

    // 3. Enviar datos al webhook
    try {
      const response = await fetch('https://n8n.srv871810.hstgr.cloud/webhook/90a1d049-6224-4d65-b417-814852f67ddc', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          ...formData, // Incluye nombre, correo, mensaje.
          recaptcha: recaptchaValue,
          fechaContacto: new Date().toLocaleDateString(),
          horaContacto: new Date().toLocaleTimeString(),
          producto: 'Formulario de Consultas' // Identificador del formulario.
        })
      });

      if (response.ok) {
        // 4. Mostrar mensaje de éxito y limpiar el formulario.
        Swal.fire('Enviado', 'El formulario se ha enviado con éxito.', 'success').then(() => {
          setFormData({ nombre: '', correo: '', mensaje: '' }); // Limpia los campos.
          recaptchaRef.current.reset(); // Resetea el reCAPTCHA.
        });
      } else {
        throw new Error('Error en el envío');
      }
    } catch (error) {
      // 5. Mostrar mensaje de error.
      Swal.fire('Error', 'Hubo un error al enviar el formulario. Por favor, intenta nuevamente.', 'error');
    }
  };

  return (
    // Layout de dos columnas en pantallas medianas y grandes.
    <div className="flex flex-col md:flex-row w-full min-h-screen bg-blue-900 py-4">
      {/* Columna izquierda: Logo (oculta en móviles). */}
      <div className="hidden md:flex md:w-1/2 items-center justify-center p-4 bg-blue-900">
        <img src={logoImage} alt="Logo DSC" className="w-3/4 md:w-1/2 lg:w-2/5 object-contain" />
      </div>

      {/* Columna derecha: Formulario con imagen de fondo. */}
      <div 
        className="w-full md:w-1/2 p-4 flex items-center justify-center" 
        style={{ backgroundImage: `url(${edificiosImage})`, backgroundSize: 'cover', backgroundPosition: 'center' }}
      >
        <div className="bg-blue-500 bg-opacity-80 rounded-2xl p-6 w-full max-w-lg">
          <h2 className="text-2xl md:text-3xl font-bold mb-6 text-center text-white">Formulario de Consultas</h2>
          <form onSubmit={handleFormSubmit} className="space-y-4">
            {/* Campos ocultos para información adicional. */}
            <input type="hidden" name="_subject" value="Nuevo mensaje del Formulario de Consultas" />
            <input type="hidden" name="producto" value="Formulario de Consultas" />

            {/* Campo Nombre */}
            <div>
              <label htmlFor="nombre" className="block mb-2 text-white">Nombre:</label>
              <input type="text" id="nombre" name="nombre" value={formData.nombre} onChange={handleChange} className="border rounded-md px-4 py-2.5 w-full" required />
            </div>

            {/* Campo Correo */}
            <div>
              <label htmlFor="correo" className="block mb-2 text-white">Correo Electrónico:</label>
              <input type="email" id="correo" name="correo" value={formData.correo} onChange={handleChange} className="border rounded-md px-4 py-2.5 w-full" required />
            </div>

            {/* Campo Mensaje */}
            <div>
              <label htmlFor="mensaje" className="block mb-2 text-white">Pregunta o consulta:</label>
              <textarea id="mensaje" name="mensaje" value={formData.mensaje} onChange={handleChange} className="border rounded-md px-4 py-2.5 w-full h-36 resize-none" required />
            </div>

            {/* Componente reCAPTCHA */}
            <div className="flex justify-center my-6">
              <ReCAPTCHA
                ref={recaptchaRef}
                sitekey="6Le3lb4pAAAAAIHJ49iYIgs-O4rowqUYrFEkbLVx" // Clave pública de reCAPTCHA.
              />
            </div>

            {/* Botón de envío */}
            <button type="submit" className="bg-blue-600 text-white py-3 px-6 rounded-md w-full">
              Enviar
            </button>
          </form>
        </div>
      </div>
    </div>
  );
};

export default FormularioGeneral;